import imageCompression from 'browser-image-compression'
import { Field } from 'formik'
import { ChangeEvent, FC, useRef } from 'react'
import { FiEdit, FiImage, FiUpload, FiVideo } from 'react-icons/fi'

import ErrorMessage from '../ErrorMessage'
import { StyleFormWrapper, StyleLabel } from '../style'
import * as S from './style'

interface UploadProps {
  variant?: 'button' | 'drag' | 'input' | 'avatar' | 'video' | 'banner'
  name: string
  placeholder?: string
  onChange?: (value: any) => void
  label?: string
  styleLabel?: string
  styleWrapper?: string
  accept?: string
  size?: number
  disabled?: boolean
  fit?: string
  isOgImage?: boolean
}

const Upload: FC<UploadProps> = ({
  variant = 'button',
  name,
  label,
  placeholder,
  onChange,
  styleLabel = '',
  styleWrapper = '',
  size = 150,
  fit,
  isOgImage,
  ...restProps
}) => {
  const uploadInputRef = useRef<HTMLInputElement>(null)
  const compressImage = async (file: File) => {
    if (!file.type.startsWith('image/')) return file
    return await imageCompression(file, {
      maxSizeMB: 3,
      maxWidthOrHeight: 1024,
      useWebWorker: true,
    })
  }

  const getFileDetails = (file: File, base64String: string) => {
    const [info, base64] = base64String.split(',')
    return {
      base64: base64.trim(),
      extension: file.name.split('.').pop()?.toLowerCase() || '',
      info,
      fileName: file.name,
    }
  }

  const handleFileChange = async (form: any, file: File | undefined) => {
    if (!file) return
    const compressedFile = await compressImage(file)
    const reader = new FileReader()
    reader.onload = (e: ProgressEvent<FileReader>) => {
      const fileData = getFileDetails(file, e.target?.result as string)
      onChange ? onChange(fileData) : form.setFieldValue(name, fileData)
    }
    reader.readAsDataURL(compressedFile)
  }

  const renderPreview = (value: any, extension: string | undefined) => {
    if (!value) return null
    const fileUrl = value?.base64
      ? `${value.info},${value.base64}`
      : value?.startsWith('https')
        ? value
        : `${process.env.APP_BUCKET_URL}/${value}`

    if (variant === 'video') {
      return <S.StyleVideoWrapper controls src={fileUrl} />
    }

    if (extension === 'pdf') {
      return <iframe src={fileUrl} height={size} width="100%" />
    }

    return (
      <S.StyleDragImageWrapper size={size} src={fileUrl} alt="upload-preview" />
    )
  }

  const renderPlaceholderIcon = () => {
    switch (variant) {
      case 'avatar':
        return <FiImage size={24} color="#64748B" />
      case 'banner':
        return <FiImage size={24} color="#64748B" />
      case 'video':
        return <FiVideo size={24} color="#64748B" />
      case 'input':
        return <FiUpload size={24} color="#64748B" />
      default:
        return <FiUpload size={16} color="#64748B" />
    }
  }

  return (
    <Field name={name}>
      {({ form, field, meta }) => {
        const hasError = meta.touched && meta.error
        const value = field.value
        const extension = value?.extension || value?.split('.')?.pop()

        const handleClick = () => uploadInputRef.current?.click()

        return (
          <StyleFormWrapper styleWrapper={styleWrapper}>
            {label && (
              <StyleLabel hasError={hasError} styleLabel={styleLabel}>
                {label}
              </StyleLabel>
            )}

            {variant === 'avatar' && (
              <S.StyleAvatarWrapper
                onClick={handleClick}
                size={size}
                hasError={hasError}
              >
                {value ? (
                  <S.StyleAvatarImage src={`${value.info},${value.base64}`} />
                ) : (
                  renderPlaceholderIcon()
                )}
                <S.StyleIconWrapper>
                  <FiEdit size={14} color="#FFFFFF" />
                </S.StyleIconWrapper>
              </S.StyleAvatarWrapper>
            )}

            {variant === 'banner' && (
              <S.StyleBannerUploadWrapper
                size={size}
                onClick={handleClick}
                isOgImage={isOgImage}
              >
                {value ? (
                  <S.StyleDragImageWrapper
                    isOgImage={isOgImage}
                    fit={fit}
                    size={size}
                    src={
                      value.info && value.base64
                        ? `${value.info},${value.base64}`
                        : typeof value === 'string'
                          ? value.startsWith('https')
                            ? value
                            : `${process.env.APP_BUCKET_URL}/${value}`
                          : ''
                    }
                  />
                ) : (
                  <S.StyleBannerUploadPlaceholder>
                    {renderPlaceholderIcon()}
                    <span>Upload image</span>
                  </S.StyleBannerUploadPlaceholder>
                )}
              </S.StyleBannerUploadWrapper>
            )}

            {variant === 'drag' && (
              <S.StyleDragUploadWrapper size={size} onClick={handleClick}>
                <S.StyleButtonRow>
                  {renderPreview(value, extension) || (
                    <>
                      {renderPlaceholderIcon()}
                      <S.StyleDragButton>
                        {placeholder || 'Drag & drop files or click to browse'}
                      </S.StyleDragButton>
                    </>
                  )}
                </S.StyleButtonRow>
              </S.StyleDragUploadWrapper>
            )}

            {variant === 'video' && (
              <S.StyleDragUploadWrapper size={size} onClick={handleClick}>
                {renderPreview(value, extension) || (
                  <>
                    {renderPlaceholderIcon()}
                    <S.StyleDragButton>
                      {placeholder || 'Upload video'}
                    </S.StyleDragButton>
                  </>
                )}
              </S.StyleDragUploadWrapper>
            )}

            {variant === 'button' && (
              <S.StyleUploadButtonWrapper
                hasError={hasError}
                onClick={handleClick}
              >
                <S.StyleUploadButton>
                  {renderPlaceholderIcon()}
                  {placeholder || 'Browse File'}
                </S.StyleUploadButton>
                {value && (
                  <S.StyleUploadButtonImgPreview
                    src={`${value.info},${value.base64}`}
                  />
                )}
              </S.StyleUploadButtonWrapper>
            )}

            {variant === 'input' && (
              <S.StyleUploadInputWrapper
                hasError={hasError}
                onClick={handleClick}
              >
                <S.StyleUploadInputFilename>
                  {value?.fileName || placeholder || 'Select a file'}
                </S.StyleUploadInputFilename>
                <FiUpload size={16} color="#64748B" />
              </S.StyleUploadInputWrapper>
            )}

            {hasError && (
              <ErrorMessage
                message={meta.error?.base64 || meta.error?.extension}
              />
            )}

            <S.StyleUploadInput
              type="file"
              ref={uploadInputRef}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                handleFileChange(form, event.target.files?.[0])
                event.target.value = ''
              }}
              accept={restProps.accept}
              disabled={restProps.disabled}
            />
          </StyleFormWrapper>
        )
      }}
    </Field>
  )
}

export default Upload
